// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import { __extends } from "tslib";
import { BaseTelemetryPlugin, arrForEach, InstrumentFunc, objForEachKey, isFunction, dateNow, isArray, isUndefined } from "@microsoft/applicationinsights-core-js";
import { Dashboard } from "./components/Dashboard";
import { getTargetName } from "./components/helpers";
import { permStyle } from "./components/styleNodeSrc";
import { DebugBin, DebugBinParent } from "./components/debugBins";
import dynamicProto from "@microsoft/dynamicproto-js";
var getDefaultConfig = function () {
    var config = {
        trackers: function () { return [
            "flush",
            "track",
            "trackEvent",
            "trackPageView",
            "trackPageViewPerformance",
            "trackException",
            "trackTrace",
            "trackMetric",
            "trackDependencyData",
            "processTelemetry",
            "throwInternal",
            "logInternalMessage",
            "triggerSend",
            "_sender",
            "perfEvent",
            "initialize"
        ]; },
        excludeKeys: function () { return [
            "_dynInstFuncs",
            "_getTelCtx",
            "_baseTelInit",
            "diagLog",
            "isInitialized",
            "setInitialized",
            "setNextPlugin",
            "processNext"
        ]; },
        cssPrefix: function () { return "ai"; },
        disableNotifications: function () { return false; },
        dumpToConsole: function () { return false; },
        maxMessages: function () { return 5000; },
        showFunctions: function () { return false; },
        logProcessTelemetry: function () { return false; }
    };
    return config;
};
var DebugPlugin = /** @class */ (function (_super) {
    __extends(DebugPlugin, _super);
    function DebugPlugin() {
        var _this = _super.call(this) || this;
        _this.identifier = DebugPlugin.identifier;
        var dashboard;
        /**
         * the style that will be permanently embedded in the webpage
         * TODO: manage style conflicts (prepend unique ID to relevant class names?)
         */
        var permStyleEl;
        /**
         * an object containing the individual debug bin items
         */
        var debugBins;
        /**
         * the parent containing all the individual debugBins
         */
        var debugBinParent;
        /**
         * the different telemetry functions that will be tracked
         */
        var trackers;
        /**
         * timestamp used to track number of seconds since webpage was loaded
         */
        var startTime = +new Date();
        /**
         * the config for this plugin
         */
        var _theConfig = getDefaultConfig();
        dynamicProto(DebugPlugin, _this, function (_self, base) {
            _self.initialize = function (config, core, extensions, pluginChain) {
                if (!_self.isInitialized()) {
                    base.initialize(config, core, extensions, pluginChain);
                    var defaultConfig = getDefaultConfig();
                    var ctx_1 = _self._getTelCtx();
                    var identifier_1 = _self.identifier;
                    objForEachKey(defaultConfig, function (field, value) {
                        _theConfig[field] = function () { return ctx_1.getConfig(identifier_1, field, value()); };
                    });
                    var foundTrackers_1 = [];
                    trackers = _theConfig.trackers();
                    var prefix = _theConfig.cssPrefix();
                    // 1. Listen to Notifications
                    if (!_theConfig.disableNotifications()) {
                        var notifyMgr = (isFunction(core.getNotifyMgr) && core.getNotifyMgr()) || core["_notificationManager"];
                        if (notifyMgr) {
                            notifyMgr.addNotificationListener({
                                eventsSent: function (events) {
                                    dashboard.newLogEntry(events, dateNow() - startTime, "Notification:eventsSent", 0, "eventsSent");
                                },
                                eventsDiscarded: function (events, reason) {
                                    dashboard.newLogEntry({
                                        events: events,
                                        reason: reason
                                    }, dateNow() - startTime, "Notification:eventsDiscarded", 0, "eventsDiscarded");
                                },
                                eventsSendRequest: function (sendReason, isAsync) {
                                    dashboard.newLogEntry({
                                        sendReason: sendReason,
                                        isAsync: isAsync
                                    }, dateNow() - startTime, "Notification:eventsSendRequest", 0, "eventsSendRequest");
                                },
                                perfEvent: function (perfEvent) {
                                    var evtName = "Notification:perfEvent[" + perfEvent.name + "]";
                                    dashboard.newLogEntry(perfEvent, dateNow() - startTime, evtName, 0, "perfEvent");
                                }
                            });
                            if (trackers.indexOf("eventsSent") !== -1) {
                                foundTrackers_1.push("eventsSent");
                            }
                            if (trackers.indexOf("eventsSendRequest") !== -1) {
                                foundTrackers_1.push("eventsSendRequest");
                            }
                            if (trackers.indexOf("eventsDiscarded") !== -1) {
                                foundTrackers_1.push("eventsDiscarded");
                            }
                            if (trackers.indexOf("perfEvent") !== -1) {
                                foundTrackers_1.push("perfEvent");
                            }
                        }
                    }
                    // 2. Get all of the extensions and channels
                    debugBins = {};
                    var targetObjects_1 = [core, _self.diagLog()];
                    // Get all of the config extensions
                    if (config.extensions) {
                        arrForEach(config.extensions, function (ext) {
                            _addTargets(targetObjects_1, ext);
                        });
                    }
                    // Get all of the passed extensions
                    if (extensions) {
                        arrForEach(extensions, function (ext) {
                            _addTargets(targetObjects_1, ext);
                        });
                    }
                    if (isFunction(core.getTransmissionControls)) {
                        var channelControls = core.getTransmissionControls();
                        if (channelControls) {
                            arrForEach(channelControls, function (channel) {
                                if (isArray(channel)) {
                                    arrForEach(channel, function (theChannel) {
                                        _addTargets(targetObjects_1, theChannel);
                                    });
                                }
                            });
                        }
                    }
                    // 3. Instrument the functions
                    arrForEach(trackers, function (tracker) {
                        arrForEach(targetObjects_1, function (target, idx) {
                            var val = InstrumentFunc(target, tracker, {
                                req: _handleInstPreHook(),
                                rsp: _handleInstPostHook()
                            }, true);
                            if (val) {
                                if (foundTrackers_1.indexOf(tracker) === -1) {
                                    foundTrackers_1.push(tracker);
                                }
                            }
                        });
                    });
                    // Sort the items
                    foundTrackers_1 = foundTrackers_1.sort();
                    // 4. Create the Dashboard
                    dashboard = new Dashboard({
                        prefix: prefix,
                        trackers: foundTrackers_1,
                        excludeKeys: _theConfig.excludeKeys(),
                        maxMessages: _theConfig.maxMessages(),
                        includeFunctions: _theConfig.showFunctions()
                    });
                    // 5. setup debugBin
                    var debugBinContainer = document.createElement("div");
                    debugBinContainer.className = prefix + "-debug-bin-container";
                    debugBinParent = new DebugBinParent(debugBinContainer, [], 0, prefix);
                    arrForEach(foundTrackers_1, function (tracker, idx) {
                        debugBins[tracker] = new DebugBin(tracker, 0, debugBinParent, (idx + 1) * 50);
                    });
                    // 6. append permanent style
                    permStyleEl = document.createElement("style");
                    permStyleEl.innerHTML = permStyle(prefix);
                    document.head.appendChild(permStyleEl);
                    // 7. add button to debugBinParent
                    debugBinParent.addButton(function (evt) {
                        evt.stopPropagation();
                        if (dashboard.isDisplayed()) {
                            dashboard.hide();
                        }
                        else {
                            dashboard.show();
                        }
                    }, "show dashboard");
                    document.body.appendChild(debugBinContainer);
                    // 8. Log the config as "keep" so it won't be dropped or cleared
                    dashboard.newLogEntry(config, 0, "config", 0, "config", true);
                }
            };
            function _addTarget(targetObjects, ext) {
                if (ext && targetObjects.indexOf(ext) === -1) {
                    targetObjects.push(ext);
                    return true;
                }
                return false;
            }
            function _addTargets(targetObjects, ext) {
                if (_addTarget(targetObjects, ext)) {
                    if (isFunction(ext["_getDbgPlgTargets"])) {
                        var extra = ext["_getDbgPlgTargets"]();
                        if (isArray(extra)) {
                            arrForEach(extra, function (tgt) {
                                _addTargets(targetObjects, tgt);
                            });
                        }
                    }
                }
            }
            function _createInstrumentObject(funcArgs, orgArgs) {
                var result = {
                    funcName: funcArgs.name,
                    inst: funcArgs.inst
                };
                if (orgArgs && orgArgs.length) {
                    result.args = orgArgs;
                }
                if (!isUndefined(funcArgs.err)) {
                    result.err = funcArgs.err;
                }
                if (!isUndefined(funcArgs.rslt)) {
                    result.rslt = funcArgs.rslt;
                }
                return result;
            }
            function _getEvtPrefix(funcArgs) {
                var identifier = getTargetName(funcArgs.inst);
                var evtPrefix = funcArgs.name;
                if (identifier) {
                    evtPrefix += ":" + identifier;
                }
                return evtPrefix;
            }
            function _handleInstPreHook() {
                return function (funcArgs) {
                    var orgArgs = [];
                    for (var _i = 1; _i < arguments.length; _i++) {
                        orgArgs[_i - 1] = arguments[_i];
                    }
                    (debugBins[funcArgs.name] || debugBins["default"]).increment();
                    if (funcArgs.name === "trackException" && !debugBinParent.showChildren) {
                        debugBinParent.addClassToEl("notify");
                    }
                    var evtPrefix = _getEvtPrefix(funcArgs);
                    dashboard.newLogEntry(_createInstrumentObject(funcArgs, orgArgs), dateNow() - startTime, "" + evtPrefix, 0, funcArgs.name);
                    if (_theConfig.dumpToConsole() && console && console.log) {
                        console.log("[" + evtPrefix + "] preProcess - funcArgs: ", funcArgs);
                        console.log("[" + evtPrefix + "] preProcess - orgArgs: ", orgArgs);
                    }
                };
            }
            function _handleInstPostHook() {
                return function (funcArgs) {
                    var orgArgs = [];
                    for (var _i = 1; _i < arguments.length; _i++) {
                        orgArgs[_i - 1] = arguments[_i];
                    }
                    if (!isUndefined(funcArgs.err)) {
                        var evtPrefix = _getEvtPrefix(funcArgs);
                        if (!debugBinParent.showChildren) {
                            debugBinParent.addClassToEl("notify");
                        }
                        // The called function threw an exception
                        dashboard.newLogEntry(_createInstrumentObject(funcArgs, orgArgs), dateNow() - startTime, "" + evtPrefix, 0, funcArgs.name);
                        if (_theConfig.dumpToConsole() && console && console.log) {
                            console.log("[" + evtPrefix + "] complete");
                        }
                    }
                };
            }
            _self.processTelemetry = function (event, itemCtx) {
                if (_theConfig.dumpToConsole() && console && console.log) {
                    console.log("[" + _self.identifier + ":processTelemetry] complete");
                }
                if (!debugBins["processTelemetry"] && _theConfig.logProcessTelemetry() === true) {
                    dashboard.newLogEntry(event, dateNow() - startTime, "[" + _self.identifier + ":processTelemetry[" + event.baseType + "]", 0, "processTelemetry");
                }
                _self.processNext(event, itemCtx);
            };
        });
        return _this;
    }
    DebugPlugin.prototype.initialize = function (config, core, extensions, pluginChain) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    DebugPlugin.prototype.processTelemetry = function (event, itemCtx) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    DebugPlugin.identifier = "DebugPlugin";
    return DebugPlugin;
}(BaseTelemetryPlugin));
export default DebugPlugin;
//# sourceMappingURL=DebugPlugin.js.map